import type {
  GetFeaturesInnerParams,
  ModalProps,
} from '@/app/components/search/components/Card/Details/Features/getFeatures/types.ts';
import type { Feature } from '@/app/components/search/components/Card/utils/facilitiesTypes.ts';
import { FeatureType } from '@/app/components/search/components/Card/utils/facilitiesTypes.ts';
import React from 'react';
import VoucherModal from '@/app/components/search/components/Modal/Modals/VoucherModal';
import type { TranslationFunction } from '@/app/components/translations/Translation.tsx';
import { formatPrice } from '@/app/components/search/utils/formatting.ts';
import type { Localization } from '@/app/components/search/common/Localization.ts';
import { Currency, VOUCHER_TYPE } from '@/domainLogic/index.ts';

type ProductVoucher = {
  bookUntil: string;
  code: string;
  description: string;
  discountCurrency: string;
  discountType: string;
  discountValue: number;
  tcUrl: string;
  uspTitle?: string;
  title: string;
};

const VOUCHER_CODES = {
  LAST_CALL: [
    'LASTCALL100',
    'LASTCALLUK',
    'LASTCALLCH',
    'LASTCALLNO',
    'LASTCALLSE',
    'LASTCALLDK',
    'LASTCALLHU',
    'LASTCALLPL',
    'LASTCALLCZ',
  ],
  SCHOOL_HOLIDAY: ['WINTER100UK', 'WINTER100'],
  VALENTINES: ['LOVE100'],
};

const getDiscountCurrency = (voucher: ProductVoucher, localization: Localization): string => {
  const hasDiscountCurrency = voucher && voucher.discountCurrency;
  const isValidCurrency = Object.values(Currency).includes(voucher.discountCurrency as Currency);
  const isDiscountCurrencyValid = hasDiscountCurrency && isValidCurrency;
  const pageCurrency = localization.currency;

  if (isDiscountCurrencyValid) return voucher.discountCurrency;
  return pageCurrency;
};

const getVoucherKey = (voucherCode: string): string => {
  if (VOUCHER_CODES.LAST_CALL.includes(voucherCode)) {
    return 'vacation.oss.voucher.usp_lastcall';
  }

  if (VOUCHER_CODES.SCHOOL_HOLIDAY.includes(voucherCode)) {
    return 'vacation.oss.voucher.usp_schoolholiday';
  }

  if (VOUCHER_CODES.VALENTINES.includes(voucherCode)) {
    return 'vacation.oss.voucher.usp_valentine';
  }

  return 'vacation.oss.voucher.usp';
};

const formatVoucherTextWithAmount = (voucherText: string, amount: string): string => {
  if (voucherText.includes('__amount__')) {
    return voucherText.replace('__amount__', amount);
  }

  return voucherText;
};

const getVoucherText = (voucher: ProductVoucher, localization: Localization, t: TranslationFunction) => {
  const isPercentage = voucher.discountType === VOUCHER_TYPE.PERCENTAGE;
  const isFixed = voucher.discountType === VOUCHER_TYPE.FIXED;

  const voucherKey = getVoucherKey(voucher.code);
  const voucherText = voucher.uspTitle ? voucher.uspTitle : t(voucherKey);

  if (isFixed) {
    const discountCurrency = getDiscountCurrency(voucher, localization);
    const formattedPrice = `${formatPrice({ price: voucher.discountValue, localization, currency: discountCurrency })}`;
    return formatVoucherTextWithAmount(voucherText, formattedPrice);
  }

  if (isPercentage) {
    return formatVoucherTextWithAmount(voucherText, `${voucher.discountValue}%`);
  }

  return null;
};

export const getVoucherFeature = (
  params: Pick<GetFeaturesInnerParams, 'localization' | 'product' | 't' | 'imageBasePath'>
): Feature[] => {
  const { product, t, localization } = params;
  const voucher = product?.promotions?.voucher;
  const voucherEnabled = localization?.configValuesApp?.voucherUspEnabled;
  const text = voucher && getVoucherText(voucher, localization, t);

  if (voucherEnabled && voucher?.code && text) {
    return [
      {
        text,
        icon: { category: 'Financial', name: 'icon-discount', color: 'iconPrimary' } as const,
        className: 'voucher',
        featureType: FeatureType.primary,
        iconRight: { category: 'Semantics', name: 'icon-circle-info', color: 'iconPrimary' } as const,
        Modal: ({ isOpen, onClose }: ModalProps) => (
          <VoucherModal isOpen={isOpen} onClose={onClose} voucher={voucher} />
        ),
      },
    ];
  }

  return [];
};
